import { ReactNode, useMemo, useState } from "react";
import { Badge, Flex, ScrollArea, Text } from "@radix-ui/themes";
import cx from "classnames";
import { Search } from "@/components/shared/inputs/search";
import styles from "./ListView.module.scss";

type ItemData = unknown | object | any[];

export type ItemRendererProps<ItemDataType extends ItemData> = {
  item: ItemDataType;
  selectedItemId?: string;
  onSelect: (itemId: string) => void;
};

interface ListViewProps<ItemDataType extends ItemData> {
  items?: ItemDataType[];
  title?: string;
  selectedItemId?: string;
  onSelect: (itemId: string) => void;
  filterFn?: (item: ItemDataType, searchPhrase: string) => boolean;
  getItemId?: (item: ItemDataType) => string;
  itemRenderer: (props: ItemRendererProps<ItemDataType>) => ReactNode;
}

export function ListView<ItemDataType extends ItemData>({
  items,
  title = "Items",
  selectedItemId,
  onSelect,
  getItemId,
  filterFn,
  itemRenderer,
}: ListViewProps<ItemDataType>) {
  const [searchPhrase, setSearchPhrase] = useState("");

  const filteredItems = useMemo(() => {
    if (items == null || filterFn == null) {
      return [];
    }

    return items.filter((item) => filterFn(item, searchPhrase));
  }, [items, filterFn, searchPhrase]);

  return (
    <Flex width="100%" height="100%" direction="column" overflow="hidden">
      <Flex p="3" direction="column" gap="2" className="border-b">
        <Flex justify="between" align="center" gap="3">
          <Text size="2" color="gray" weight="medium" highContrast>
            {title}
          </Text>

          {items != null && items.length > 0 && <Badge>{items.length}</Badge>}
        </Flex>

        <Search
          initialValue={searchPhrase}
          placeholder={`Search ${title}`}
          setSearchPhrase={setSearchPhrase}
          debounce={50}
          size="2"
        />
      </Flex>

      <Flex
        flexGrow="1"
        direction="column"
        gap="1"
        className={styles.listView}
        overflow="hidden"
      >
        <Flex height="100%" overflow="hidden">
          <ScrollArea
            scrollbars={"vertical"}
            style={{ width: "100%", height: "100%" }}
          >
            <Flex p="3" height="100%" direction="column" gap="1">
              {filteredItems?.length === 0 && (
                <Flex flexGrow="1" p="4" justify="center">
                  <Text size="2" color="gray">
                    No Items Found
                  </Text>
                </Flex>
              )}

              {filteredItems?.map((item, idx) => {
                const itemId = getItemId?.(item) ?? idx.toString();

                return (
                  <Flex
                    key={`item-${itemId}`}
                    onClick={() => onSelect(itemId)}
                    className={cx(styles.listItem, {
                      [styles.active]: selectedItemId === itemId,
                    })}
                  >
                    {itemRenderer({
                      item,
                      selectedItemId,
                      onSelect,
                    })}
                  </Flex>
                );
              })}
            </Flex>
          </ScrollArea>
        </Flex>
      </Flex>
    </Flex>
  );
}
